import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useGetAllListingQuery, useRecalcScoreQuery } from '../../services/Listing';
import { mapListing, getVerdictFromRules } from '../../utils/mapListing';
import { fmtPrice, fmtCf } from '../Dashboard/Dashboard';
import { useRules } from '../../hooks/useRules';
import '../../assets/css/vela.css';

const BRAND = '#1DB3B3';
const CYAN = '#1DB3B3';
const CYAN_HOVER = '#179999';

// Card-Badge-Farben nach Rules-Key (top/good/check/risky/pass)
const VC = {
  top:   { bg: "var(--green-l)", br: "var(--green-b)", tx: "var(--green-d)" },
  good:  { bg: "var(--teal-l)",  br: "var(--teal-b)",  tx: "var(--teal-d)" },
  check: { bg: "var(--amber-l)", br: "var(--amber-b)", tx: "var(--amber)" },
  risky: { bg: "var(--amber-l)", br: "var(--amber-b)", tx: "var(--amber)" },
  pass:  { bg: "var(--s100)",    br: "var(--s200)",    tx: "var(--s500)" },
};

/* ── Score-Badge for Deal-Picker ── */
function PickerScoreBadge({ score }) {
  if (score === null || score === undefined) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '2px 8px', borderRadius: 99,
        fontSize: 12, fontWeight: 600,
        background: '#E0E0DF', color: '#838799',
      }}>--</span>
    );
  }
  const style = score >= 65
    ? { bg: '#E8F7E6', tx: '#1FA534' }
    : score >= 40
    ? { bg: '#FCF7E6', tx: '#F0C045' }
    : { bg: '#FDEAEA', tx: '#E83838' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 99,
      fontSize: 12, fontWeight: 600,
      background: style.bg, color: style.tx,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {score}
    </span>
  );
}

/* ── Deal-Picker Component ── */
function DealPicker({ allDeals, selectedIds, onToggle }) {
  const count = selectedIds.size;
  const maxReached = count >= 4;

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E0E0DF',
      borderRadius: 12,
      padding: 16,
      marginBottom: 20,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12,
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1F1F1F' }}>
          Deals auswählen
        </span>
        <span style={{
          fontSize: 14,
          fontWeight: 400,
          color: maxReached ? '#F0C045' : '#838799',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}>
          {maxReached && (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
              <path d="M7 2L12.5 11.5H1.5L7 2Z" stroke="#F0C045" strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
              <path d="M7 6v2.5" stroke="#F0C045" strokeWidth="1.3" strokeLinecap="round"/>
              <circle cx="7" cy="10" r=".6" fill="#F0C045"/>
            </svg>
          )}
          {count} von 4 ausgewählt
        </span>
      </div>

      {/* Deal rows */}
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {allDeals.map((deal, idx) => {
          const isChecked = selectedIds.has(deal.id);
          const isDisabled = !isChecked && maxReached;
          const isLast = idx === allDeals.length - 1;

          return (
            <label
              key={deal.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                height: 40,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                borderBottom: isLast ? 'none' : '1px solid #E0E0DF',
                padding: '0 8px',
                borderRadius: 6,
                opacity: isDisabled ? 0.4 : 1,
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.background = '#F3F3F5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Custom Checkbox */}
              <span
                style={{
                  width: 16, height: 16, flexShrink: 0,
                  borderRadius: 4,
                  border: isChecked ? `1.5px solid ${CYAN}` : '1.5px solid #ACACB8',
                  background: isChecked ? CYAN : 'transparent',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.12s',
                }}
              >
                {isChecked && (
                  <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                    <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => onToggle(deal.id)}
                style={{ display: 'none' }}
              />

              {/* Address + City */}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{
                  fontSize: 14, fontWeight: 500, color: '#1F1F1F',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  display: 'block',
                }}>
                  {deal.address}
                  {deal.city && (
                    <span style={{ fontSize: 12, fontWeight: 400, color: '#ACACB8', marginLeft: 6 }}>
                      {deal.city}
                    </span>
                  )}
                </span>
              </span>

              {/* Score Badge */}
              <PickerScoreBadge score={deal.score} />

              {/* Cashflow */}
              <span style={{
                fontSize: 14, fontWeight: 400, color: deal.cf !== null && deal.cf >= 0 ? '#1FA534' : '#5D5D6D',
                textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0,
                minWidth: 80,
              }}>
                {deal.cf !== null ? (deal.cf >= 0 ? '+' : '') + deal.cf.toLocaleString('de-DE') + '\u00A0€/Mo.' : '\u2014'}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ── Mode Toggle ── */
function ModeToggle({ mode, onChange }) {
  const segments = [
    { key: 'auto',   label: 'Top 4 nach Score' },
    { key: 'manual', label: 'Eigene Auswahl' },
  ];
  return (
    <div style={{
      display: 'inline-flex',
      background: '#E0E0DF',
      borderRadius: 24,
      padding: 3,
      marginBottom: 20,
      height: 36,
      alignItems: 'center',
    }}>
      {segments.map(seg => {
        const isActive = mode === seg.key;
        return (
          <button
            key={seg.key}
            onClick={() => onChange(seg.key)}
            style={{
              padding: '8px 16px',
              borderRadius: 20,
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 14,
              lineHeight: '20px',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? '#1F1F1F' : '#838799',
              background: isActive ? '#FFFFFF' : 'transparent',
              boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              transition: 'all 0.12s',
              whiteSpace: 'nowrap',
            }}
          >
            {seg.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Warn Hint: < 2 deals selected ── */
function MinSelectionHint() {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: '#FCF7E6',
      border: '1px solid #F0C045',
      borderRadius: 8,
      padding: '12px 16px',
      marginTop: 4,
    }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
        <path d="M8 2.5L14 13H2L8 2.5Z" stroke="#F0C045" strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
        <path d="M8 6.5V9.5" stroke="#F0C045" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="8" cy="11.5" r=".7" fill="#F0C045"/>
      </svg>
      <span style={{ fontSize: 14, fontWeight: 400, color: '#5D5D6D', lineHeight: '20px' }}>
        Mindestens 2 Deals auswählen, um den Vergleich anzuzeigen.
      </span>
    </div>
  );
}

/**
 * useRecalcDeals — loads live-calculated KPIs for up to 4 deals via recalc endpoint.
 * RTK Query hooks cannot be called in loops, so we use 4 fixed slots with skip.
 * Returns a Map<listingId, { cf, ekRendite, loading }>.
 */
function useRecalcDeals(dealIds) {
  // Exactly 4 fixed slots — hook call count is constant
  const id0 = dealIds[0] ?? null;
  const id1 = dealIds[1] ?? null;
  const id2 = dealIds[2] ?? null;
  const id3 = dealIds[3] ?? null;

  const r0 = useRecalcScoreQuery(id0, { skip: !id0 });
  const r1 = useRecalcScoreQuery(id1, { skip: !id1 });
  const r2 = useRecalcScoreQuery(id2, { skip: !id2 });
  const r3 = useRecalcScoreQuery(id3, { skip: !id3 });

  return useMemo(() => {
    const map = new Map();
    const slots = [
      { id: id0, result: r0 },
      { id: id1, result: r1 },
      { id: id2, result: r2 },
      { id: id3, result: r3 },
    ];
    for (const { id, result } of slots) {
      if (!id) continue;
      const cf = result.data?.cashflow;
      const sc = result.data?.score;
      map.set(String(id), {
        score: sc?.total ?? null,
        cf: cf?.vorTilgung ?? null,
        cfNachTilgung: cf?.nachTilgung ?? null,
        ekRendite: cf?.ekRendite ?? null,
        loading: result.isLoading || result.isFetching,
        hasData: !!cf,
      });
    }
    return map;
  // eslint-disable-next-line
  }, [id0, id1, id2, id3, r0.data, r1.data, r2.data, r3.data,
      r0.isLoading, r1.isLoading, r2.isLoading, r3.isLoading]);
}

/* ── Main Component ── */
export default function Comparison() {
  const rules    = useRules();
  const navigate = useNavigate();
  const location = useLocation();
  const auth     = useAuth();
  const userID   = auth.user?.profile?.sub;

  // Read preselected IDs from URL query params (?ids=123,456,789)
  const preselectedIds = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw    = params.get('ids');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [location.search]);

  // If preselectedIds are present: start in manual mode with those IDs selected
  const [mode, setMode] = useState(() => preselectedIds.length >= 2 ? 'manual' : 'auto');
  const [selectedIds, setSelectedIds] = useState(() =>
    preselectedIds.length > 0 ? new Set(preselectedIds) : new Set()
  );

  const { data, isLoading, isError } = useGetAllListingQuery(userID, {
    skip: !userID,
  });

  const rawListings = data?.listings || [];
  const allDeals = rawListings.map(mapListing).sort((a, b) => (b.score || 0) - (a.score || 0));

  // Once deals are loaded: validate preselected IDs actually exist in the user's deals.
  // If a preselected ID doesn't match any deal, silently drop it.
  useEffect(() => {
    if (preselectedIds.length === 0 || allDeals.length === 0) return;
    const validIds = new Set(allDeals.map(d => String(d.id)));
    const sanitized = preselectedIds.filter(id => validIds.has(String(id)));
    if (sanitized.length !== preselectedIds.length) {
      setSelectedIds(new Set(sanitized));
    }
  // Only run when deals first load — not on every re-render
  // eslint-disable-next-line
  }, [allDeals.length]);

  // Determine which deals are shown in comparison.
  // selectedIds may contain string IDs (from URL params) or numeric IDs (from manual picker),
  // so compare as strings to avoid type mismatch.
  const shown = mode === 'auto'
    ? allDeals.slice(0, 4)
    : allDeals.filter(d => selectedIds.has(d.id) || selectedIds.has(String(d.id)));

  // Live-calculated KPIs via recalc endpoint (max 4 deals)
  const shownIds = shown.map(d => d.id);
  const recalcMap = useRecalcDeals(shownIds);

  // Merge recalc values into deal objects — fall back to DB value if recalc not yet loaded
  const shownWithLiveKpis = useMemo(() => shown.map(d => {
    const live = recalcMap.get(String(d.id));
    return {
      ...d,
      score:     live?.score != null ? live.score : d.score,
      cf:        live?.hasData ? live.cf        : d.cf,
      ekRendite: live?.hasData ? live.ekRendite  : d.ekRendite,
      kpiSource: live?.hasData ? 'recalc' : 'db',
    };
  // eslint-disable-next-line
  }), [shown, recalcMap]);

  const winner = shownWithLiveKpis[0];

  const handleToggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 4) {
        next.add(id);
      }
      return next;
    });
  };

  // ── Loading state
  if (isLoading) return (
    <div className="cmp-wrap">
      <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--s400)', fontSize: 14 }}>
        Vergleich wird geladen{'\u2026'}
      </div>
    </div>
  );

  // ── Error / no deals at all
  if (isError || allDeals.length === 0) return (
    <div className="cmp-wrap">
      <button className="back-btn" onClick={() => navigate('/properties')}>
        {'\u2190'} Zur{'ü'}ck
      </button>
      <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--s400)', fontSize: 14 }}>
        {allDeals.length === 0 ? 'Noch keine Objekte zum Vergleichen gespeichert.' : 'Fehler beim Laden.'}
      </div>
    </div>
  );

  // Metrics use shownWithLiveKpis so recalc values are compared, not DB estimates
  const metrics = [
    { label: "Score",          f: d => d.score || 0,      best: a => a.reduce((x, y) => (x.score || 0) > (y.score || 0) ? x : y).id, worst: a => a.reduce((x, y) => (x.score || 0) < (y.score || 0) ? x : y).id, fmt: v => `${v}` },
    { label: "Cashflow / Mo.", f: d => d.cf || 0,         best: a => a.reduce((x, y) => (x.cf || 0) > (y.cf || 0) ? x : y).id,       worst: a => a.reduce((x, y) => (x.cf || 0) < (y.cf || 0) ? x : y).id,       fmt: v => fmtCf(v) },
    { label: "Kaufpreis",      f: d => d.price,           best: a => a.reduce((x, y) => x.price < y.price ? x : y).id,               worst: a => a.reduce((x, y) => x.price > y.price ? x : y).id,               fmt: v => `${fmtPrice(v)} €` },
    { label: "EK-Rendite",    f: d => d.ekRendite || 0,   best: a => a.reduce((x, y) => (x.ekRendite || 0) > (y.ekRendite || 0) ? x : y).id, worst: a => a.reduce((x, y) => (x.ekRendite || 0) < (y.ekRendite || 0) ? x : y).id, fmt: v => `${parseFloat(v).toFixed(1)} %` },
  ];

  // Dynamic column count for table header/rows
  const colCount = shownWithLiveKpis.length;
  const gridTemplate = `145px repeat(${colCount}, 1fr)`;

  const manualNotEnough = mode === 'manual' && shown.length < 2;

  // Check if any recalc is still loading (for cards loading indicator)
  const anyRecalcLoading = shownIds.some(id => recalcMap.get(String(id))?.loading);

  return (
    <div className="cmp-wrap">
      <button className="back-btn" onClick={() => navigate('/properties')}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 6H3M6 3L3 6l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Zur{'ü'}ck
      </button>

      <div className="cmp-decision-headline">
        <div className="cmp-dh-q">Welches Objekt ist das beste Investment?</div>
        <div className="cmp-dh-sub">
          {mode === 'auto'
            ? `${shownWithLiveKpis.length} Deals im Direktvergleich \u2014 Score, Cashflow und Kaufpreis.`
            : `Eigene Auswahl \u2014 Score, Cashflow und Kaufpreis.`}
        </div>
      </div>

      {/* Mode Toggle */}
      <ModeToggle mode={mode} onChange={(m) => {
        setMode(m);
        // Pre-select top 4 when switching to manual so user has a starting point
        if (m === 'manual' && selectedIds.size === 0) {
          setSelectedIds(new Set(allDeals.slice(0, 4).map(d => d.id)));
        }
      }} />

      {/* Deal Picker — only in manual mode */}
      <div style={{
        overflow: 'hidden',
        maxHeight: mode === 'manual' ? 400 : 0,
        opacity: mode === 'manual' ? 1 : 0,
        transition: 'max-height 200ms ease, opacity 200ms ease',
      }}>
        <DealPicker
          allDeals={allDeals}
          selectedIds={selectedIds}
          onToggle={handleToggle}
        />
      </div>

      {/* Warn hint when manual mode + < 2 selected */}
      {manualNotEnough && <MinSelectionHint />}

      {/* Comparison content — shown when auto OR when manual + >= 2 deals */}
      {!manualNotEnough && shownWithLiveKpis.length >= 2 && (
        <>
          {/* Recommendation block */}
          {winner && (
            <div className="rec-block" style={{ marginTop: mode === 'manual' ? 20 : 0 }}>
              <div className="rec-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2L12.5 8H18.5L13.5 11.5L15.5 18L10 14.5L4.5 18L6.5 11.5L1.5 8H7.5L10 2Z" fill="white" opacity=".85" /></svg>
              </div>
              <div className="rec-body">
                <div className="rec-overline">Empfehlung</div>
                <div className="rec-title">{winner.address} ist derzeit die st{'ä'}rkste Option.</div>
                <div className="rec-text">{getVerdictFromRules(rules, winner.score)?.verdict_sentence ?? ''}</div>
              </div>
            </div>
          )}

          {/* Compare cards — dynamic column count */}
          <div className="cmp-cards" style={{
            gridTemplateColumns: colCount === 2 ? 'repeat(2,1fr)'
              : colCount === 3 ? 'repeat(3,1fr)'
              : 'repeat(4,1fr)',
          }}>
            {shownWithLiveKpis.map((d) => {
              const isW = winner && d.id === winner.id;
              const verdictCard = getVerdictFromRules(rules, d.score);
              const vkey = verdictCard?.key ?? 'check';
              const isMuted = vkey === 'pass';
              const c = VC[vkey] || VC.check;
              const live = recalcMap.get(String(d.id));
              return (
                <div key={d.id} className={`cmp-card${isW ? " winner" : ""}${isMuted ? " muted" : ""}`} onClick={() => navigate(`/properties/${d.id}`)}>
                  {isW && <div className="cmp-winner-bar">EMPFEHLUNG</div>}
                  <div className="cmp-top">
                    <div className="cmp-score-row">
                      <div><span className="cmp-score-n">{d.score || '\u2014'}</span><span className="cmp-score-d"> /100</span></div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 99, fontSize: '9.5px', fontWeight: 700, background: c.bg, border: `1px solid ${c.br}`, color: c.tx }}>
                        {(getVerdictFromRules(rules, d.score)?.verdict_sentence ?? '').split('\u2014')[0].trim()}
                      </span>
                    </div>
                    <div className="cmp-addr">{d.address}</div>
                    <div className="cmp-loc">{d.city}</div>
                  </div>
                  <div className="cmp-body">
                    <div className="cmp-cf-row">
                      <span className="cmp-cf-l" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        Cashflow
                        {/* Show source hint only while recalc is still loading */}
                        {live?.loading && (
                          <span style={{ fontSize: 10, color: BRAND, fontWeight: 400, opacity: 0.8 }}>
                            \u2026
                          </span>
                        )}
                      </span>
                      <span className="cmp-cf-v" style={{ color: d.cf !== null && d.cf >= 0 ? 'var(--green)' : 'var(--s700)' }}>
                        {d.cf !== null ? fmtCf(d.cf) : '\u2014'}
                      </span>
                    </div>
                    <button className="cmp-open" onClick={(e) => { e.stopPropagation(); navigate(`/properties/${d.id}`); }}>Deal pr{'ü'}fen {'\u2192'}</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comparison table — dynamic column count */}
          <div className="cmp-table-wrap">
            <div className="cmp-table-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Detailvergleich
              {/* KPI source hint: shown if all recalc data is loaded */}
              {!anyRecalcLoading && shownWithLiveKpis.some(d => d.kpiSource === 'recalc') && (
                <span
                  title="Cashflow und EK-Rendite wurden live berechnet — identisch mit dem Workspace."
                  style={{
                    fontSize: 11, fontWeight: 500, color: BRAND,
                    border: `1px solid ${BRAND}`, borderRadius: 99,
                    padding: '1px 7px', cursor: 'help', letterSpacing: 0.1,
                  }}
                >
                  Live-KPIs
                </span>
              )}
              {anyRecalcLoading && (
                <span style={{ fontSize: 11, color: '#ACACB8', fontStyle: 'italic' }}>
                  KPIs werden berechnet\u2026
                </span>
              )}
            </div>
            <div className="mt-table">
              <div className="mt-hdr" style={{ gridTemplateColumns: gridTemplate }}>
                <div className="mt-hdr-c" style={{ textAlign: 'left' }}>Kennzahl</div>
                {shownWithLiveKpis.map(d => (
                  <div key={d.id} className={`mt-hdr-c ${winner && d.id === winner.id ? 'win' : ''}`}>
                    {d.address.length > 15 ? d.address.slice(0, 14) + '\u2026' : d.address}
                  </div>
                ))}
              </div>
              {metrics.map((m, mi) => {
                const bi = m.best(shownWithLiveKpis);
                const wi = m.worst(shownWithLiveKpis);
                return (
                  <div key={mi} className="mt-row" style={{ gridTemplateColumns: gridTemplate }}>
                    <div className="mt-row-l">{m.label}</div>
                    {shownWithLiveKpis.map(d => (
                      <div key={d.id} className={`mt-cell ${d.id === bi ? 'best' : d.id === wi ? 'worst' : ''}`}>
                        {m.fmt(m.f(d))}
                      </div>
                    ))}
                  </div>
                );
              })}
              <div className="mt-row" style={{ gridTemplateColumns: gridTemplate }}>
                <div className="mt-row-l">Urteil</div>
                {shownWithLiveKpis.map(d => {
                  const verdictObj = getVerdictFromRules(rules, d.score);
                  const v = verdictObj ? verdictObj.label : 'Abwägen';
                  // Badge-Klasse: gut→buy, Prüfen/Riskant→caut, Abraten→pass
                  const vkey = verdictObj?.key;
                  const badgeCls = (vkey === 'top' || vkey === 'good') ? 'buy'
                    : (vkey === 'pass') ? 'pass' : 'caut';
                  return (
                    <div key={d.id} className="mt-cell">
                      <span className={`mtbadge ${badgeCls}`}>{v}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
